import { useState } from "react";
import {
  Sparkles, Loader2, MessageSquare, ListChecks, HelpCircle, Send, Lightbulb, ClipboardCheck, ArrowLeft,
} from "lucide-react";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

interface Prep {
  overview: string;
  suggestedResponses: string[];
  keyPoints: string[];
  potentialClientQuestions: string[];
  questionsToAsk: string[];
  recommendations: string[];
}

// Predefined interview topics shown as selectable bubbles.
const TOPICS = [
  "Introduction & Background",
  "Relevant Experience",
  "Technical Skills",
  "Project Approach & Process",
  "Portfolio & Case Studies",
  "Pricing & Budget",
  "Timeline & Availability",
  "Communication Style",
  "Team & Collaboration",
  "Handling Revisions",
  "Post-Delivery Support",
  "Why Choose Us",
];

export default function InterviewPrepPage() {
  const [selected, setSelected] = useState<string[]>([]);
  const [context, setContext] = useState("");
  const [phase, setPhase] = useState<"input" | "loading" | "result">("input");
  const [prep, setPrep] = useState<Prep | null>(null);
  const [ai, setAi] = useState(false);

  const toggle = (t: string) =>
    setSelected((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const generate = async () => {
    if (!selected.length && !context.trim()) {
      toast.error("Pick at least one topic or add some context.");
      return;
    }
    setPhase("loading");
    try {
      const { data } = await axiosInstance.post("/interview/prepare", {
        topics: selected,
        context: context.trim(),
      });
      setPrep(data.prep);
      setAi(!!data.aiGenerated);
      setPhase("result");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't prepare interview");
      setPhase("input");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Sparkles className="text-[#2462b2]" size={22} /> Interview Preparation
        </h1>
        <p className="text-sm text-slate-500 mt-1">Pick what the interview will cover, add any context, and get AI-tailored prep.</p>
      </div>

      {phase === "input" && (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">What will the interview cover?</h3>
            <div className="flex flex-wrap gap-2">
              {TOPICS.map((t) => {
                const on = selected.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggle(t)}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      on
                        ? "bg-[#2462b2] text-white border-[#2462b2] shadow-sm"
                        : "bg-white text-slate-600 border-slate-300 hover:border-[#2462b2]/50"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && <p className="text-[11px] text-slate-400 mt-2">{selected.length} selected</p>}
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">Add context <span className="font-normal text-slate-400">(optional)</span></h3>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={5}
              placeholder="e.g. Client interview for a Shopify store redesign, fixed budget ~$4k, they want a demo in 4 weeks and are worried about SEO. My strength is front-end..."
              className="w-full border border-slate-300 rounded-xl px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] transition"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={generate}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2462b2] text-white text-sm font-semibold hover:bg-[#1e52a0]"
            >
              <Sparkles size={16} /> Prepare Me
            </button>
          </div>
        </div>
      )}

      {phase === "loading" && (
        <div className="py-20 grid place-items-center gap-3 text-slate-500">
          <Loader2 size={30} className="animate-spin text-[#2462b2]" />
          <p className="text-sm">Preparing your interview guidance…</p>
        </div>
      )}

      {phase === "result" && prep && (
        <div className="space-y-5">
          {!ai && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">AI unavailable — showing general guidance.</p>}

          <Section icon={ClipboardCheck} title="Interview Overview">
            <p className="text-sm text-slate-700 leading-relaxed">{prep.overview}</p>
          </Section>
          <Bullets icon={MessageSquare} title="Suggested Responses" items={prep.suggestedResponses} />
          <Bullets icon={ListChecks} title="Key Discussion Points" items={prep.keyPoints} />
          <Bullets icon={HelpCircle} title="Potential Client Questions" items={prep.potentialClientQuestions} />
          <Bullets icon={Send} title="Questions You Should Ask" items={prep.questionsToAsk} />
          <Bullets icon={Lightbulb} title="Recommendations" items={prep.recommendations} highlight />

          <div className="flex justify-between pt-2">
            <button onClick={() => setPhase("input")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              <ArrowLeft size={15} /> Adjust topics
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, title, children }: { icon: any; title: string; children: any }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2"><Icon size={16} className="text-[#2462b2]" /> {title}</h3>
    {children}
  </div>
);
const Bullets = ({ icon: Icon, title, items, highlight }: { icon: any; title: string; items: string[]; highlight?: boolean }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2"><Icon size={16} className={highlight ? "text-amber-500" : "text-[#2462b2]"} /> {title}</h3>
    <ul className="space-y-1.5">
      {(items || []).map((it, i) => (
        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${highlight ? "bg-amber-400" : "bg-[#2462b2]"}`} /> {it}
        </li>
      ))}
    </ul>
  </div>
);
